import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { List } from "immutable";
import { SubSink } from "subsink";
import { TimelineItem } from "../../../../../shared/timeline/timeline-item.model";
import { ArrayHelper } from "../../../../../utilities/contracts/array-helper";
import { DateHelper } from "../../../../../utilities/contracts/date-helper";
import { StringHelper } from "../../../../../utilities/contracts/string-helper";
import { IMemberHistoryEvent } from "../../../../api/member-v2/member-history-response.model";
import { MemberV2Service } from "../../../../api/member-v2/member-v2.service";
import { RetrievalTimelineItem } from "../../../retrieval/retrieval-timeline-item.model";

@Component({
  selector: "app-member-timeline",
  templateUrl: "./member-timeline.component.html",
  styleUrls: ["./member-timeline.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MemberTimelineComponent implements OnInit, OnDestroy {
  private sink = new SubSink();
  memRecordId = "";
  timelineGroups = List<RetrievalTimelineItem>();
  timelineLoading = false;
  timelineLoaded = false;

  constructor(
    private route: ActivatedRoute,
    private memberService: MemberV2Service,
    private readonly cd: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.sink.add(
      this.route.paramMap.subscribe(params => {
        this.memRecordId = params.get("memRecordId") ?? "";
        this.loadTimeline();
      })
    );
  }

  ngOnDestroy(): void {
    this.sink.unsubscribe();
  }

  private loadTimeline(): void {
    if (!StringHelper.isAvailable(this.memRecordId)) {
      this.timelineLoaded = true;
      return;
    }

    this.timelineLoading = true;
    this.sink.add(
      this.memberService.getMemberHistory(this.memRecordId).subscribe(
        res => {
          const events = (res?.events ?? [])
            .slice()
            .sort((a, b) => new Date(b.eventDateTime).getTime() - new Date(a.eventDateTime).getTime());
          const timelineItems = events.map(e => this.toTimelineItem(e));
          this.timelineGroups = List(this.groupTimelineItemsByDay(timelineItems));
          this.timelineLoading = false;
          this.timelineLoaded = true;
          this.cd.markForCheck();
        },
        () => {
          this.timelineLoading = false;
          this.timelineLoaded = true;
          this.cd.markForCheck();
        }
      )
    );
  }

  private toTimelineItem(event: IMemberHistoryEvent): TimelineItem {
    return new TimelineItem({
      type: this.eventIcon(event.event),
      title: this.eventLabel(event.event),
      details: this.eventDetails(event),
      username: StringHelper.isAvailable(event.userName) ? event.userName : "System",
      timestamp: event.eventDateTime,
    });
  }

  private groupTimelineItemsByDay(items: TimelineItem[]): RetrievalTimelineItem[] {
    const grouped = items.reduce<{ [day: string]: TimelineItem[] }>((acc, item) => {
      const day = DateHelper.format(item.timestamp);
      acc[day] = acc[day] || [];
      acc[day].push(item);
      return acc;
    },                                                              {});

    return Object.keys(grouped).map(specificDay => new RetrievalTimelineItem({
      specificDay,
      timelineGroup: List(grouped[specificDay]),
    }));
  }

  private eventLabel(event: string): string {
    switch (event) {
      case "CREATE": return "Member created";
      case "MERGE":  return "Member merged";
      case "RESOLVE_NOT_DUPLICATES": return "Resolved as not a duplicate";
      default:       return event;
    }
  }

  private eventIcon(event: string): string {
    switch (event) {
      case "CREATE": return "plus";
      case "MERGE":  return "code-merge";
      case "RESOLVE_NOT_DUPLICATES": return "object-ungroup";
      default:       return "circle";
    }
  }

  private eventDetails(event: IMemberHistoryEvent): string {
    if (event.event === "MERGE") {
      return ArrayHelper.isAvailable(event.mergedWithRecordIds)
        ? `member merged with member(s) ${event.mergedWithRecordIds.join(", ")}`
        : "";
    }
    if (event.event === "RESOLVE_NOT_DUPLICATES") {
      const ids = ArrayHelper.isAvailable(event.resolvedWithRecordIds)
        ? event.resolvedWithRecordIds
        : event.resolvedNotDuplicatesAdded;
      return ArrayHelper.isAvailable(ids)
        ? `resolved as not a duplicate of ${ids.join(", ")}`
        : "";
    }
    return "";
  }
}
